/**
 * HairDemoContent - EXACT replica of phygital.systems/hair landing page
 * Used as demo tab content for Hair pilot invites
 * All text and images are editable by super admin
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { useAdmin, EditableText, EditableImage } from "@/components/AdminContentEditor";
import {
  ArrowRight,
  MessageCircle,
  Star,
  Check,
  ChevronRight,
  Image as ImageIcon,
  Download,
  Smartphone,
  Clock,
  MapPin,
  Send
} from "lucide-react";

export default function HairDemoContent({ onAccept, portfolioImages = [] }) {
  const { editMode } = useAdmin();
  const [showPrices, setShowPrices] = useState(true);

  // Booking flow steps from exact PDF
  const flowSteps = [
    { num: 1, title: "Client DMs You", desc: "Find you on IG, text or DM to book" },
    { num: 2, title: "Send Preference Link", desc: "Create booking, send phygital link" },
    { num: 3, title: "Client Fills Preferences", desc: "Style, length, vibe all captured" },
    { num: 4, title: "You Confirm & Price", desc: "Review, set final price in app" },
    { num: 5, title: "Reminder Sent", desc: "Day before: client confirms" },
    { num: 6, title: "Address Released", desc: "Location sent after confirmation" }
  ];

  // Services from exact PDF
  const services = [
    { name: "Knotless Braids", price: "$180+", selected: true },
    { name: "Corn Rows", price: "$80+" },
    { name: "Lemonade Braids", price: "$200" },
    { name: "Tribal Braids", price: "$220+" },
    { name: "Quick Weaves", price: "" }
  ];

  // Look options from exact PDF
  const looks = [
    { name: "Classic Medium", tag: "Popular" },
    { name: "Boho Style", tag: "" },
    { name: "Jumbo", tag: "" },
    { name: "Bob Length", tag: "" },
    { name: "Add Inspo", tag: "" },
    { name: "Custom", tag: "" }
  ];

  // Length customization from exact PDF
  const lengths = [
    { name: "Bob/Shoulder", price: "" },
    { name: "Mid Back", price: "+$30" },
    { name: "Lower Back", price: "+$60" },
    { name: "Below Butt", price: "+$100" }
  ];

  // Hair shades from exact PDF
  const shades = [
    { code: "1", name: "Jet Black" },
    { code: "2B", name: "Dark Brown" },
    { code: "1B", name: "Off Black" },
    { code: "27", name: "Honey Blonde" },
    { code: "2", name: "Darkest Brown" },
    { code: "4", name: "Medium Brown" }
  ];

  // Vibe preferences from exact PDF
  const vibes = [
    { label: "Chat away", desc: "Love conversation" },
    { label: "Quiet please", desc: "Prefer silence" },
    { label: "Little chat", desc: "Some conversation" },
    { label: "Tired (sleep ok)", desc: "May doze off" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Exact from PDF */}
      <section className="py-16 px-4 bg-gradient-to-b from-pink-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <Badge className="mb-4 bg-pink-100 text-pink-700">
              Hair Pilot by Phygital
            </Badge>
            <EditableText
              sectionId="hair-demo-hero"
              field="title"
              defaultValue="Styles Booked Seamlessly"
              className="text-4xl sm:text-5xl font-bold mb-4"
              as="h1"
            />
            <EditableText
              sectionId="hair-demo-hero"
              field="subtitle"
              defaultValue="Clients find you on IG, DM to book. You send them a link, they fill their preferences, you confirm the price. Address released only after they confirm."
              className="text-gray-600 max-w-2xl mx-auto text-lg"
              as="p"
            />
          </div>

          {/* Feature tags from PDF */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {["IG to Booked", "iCloud sync", "Address after confirm", "Story generator"].map(tag => (
              <Badge key={tag} variant="secondary" className="bg-white border">
                {tag}
              </Badge>
            ))}
          </div>

          <div className="flex justify-center">
            <Button onClick={onAccept} className="bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-full px-8 h-12">
              See the Flow
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Booking Flow Section - Exact from PDF */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-2">Your Booking Flow</h2>
            <p className="text-gray-600">From DM to Done</p>
            <p className="text-sm text-gray-500 mt-2">No booking platform needed. Clients text or DM you, you handle the rest.</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {flowSteps.map((step, i) => (
              <div key={i} className="text-center p-4 rounded-xl bg-gray-50 border">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 text-white flex items-center justify-center mx-auto mb-3 text-lg font-bold">
                  {step.num}
                </div>
                <h3 className="font-semibold text-sm mb-1">{step.title}</h3>
                <p className="text-xs text-gray-500">{step.desc}</p>
              </div>
            ))}
          </div>

          {/* Example DM interaction from PDF */}
          <div className="max-w-md mx-auto mt-12 bg-gray-50 rounded-2xl p-6 border">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-pink-400 to-purple-400 flex items-center justify-center text-white font-bold">
                M
              </div>
              <div>
                <p className="font-semibold">Maya</p>
              </div>
            </div>
            <div className="bg-white rounded-xl p-3 border mb-2">
              <p className="text-sm">Hey! I saw your knotless braids and I'm obsessed 😍</p>
            </div>
            <div className="bg-white rounded-xl p-3 border">
              <p className="text-sm">Do you have any openings this weekend?</p>
            </div>
            <div className="flex gap-2 mt-4">
              {["Text", "DM", "iMessage"].map(type => (
                <Badge key={type} variant="outline" className="text-xs">{type}</Badge>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Service Menu Section - Exact from PDF */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-pink-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-2">Service Menu</h2>
            <p className="text-gray-600">Select a Style</p>
            <p className="text-sm text-gray-500">Browse styles, see preset looks, customize your options, and get what you need to bring.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Services List */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-gray-600">Show prices to clients?</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{showPrices ? "Yes" : "No"}</span>
                    <Switch checked={showPrices} onCheckedChange={setShowPrices} />
                  </div>
                </div>
                <div className="space-y-2">
                  {services.map((service, i) => (
                    <div 
                      key={i} 
                      className={`flex items-center justify-between p-3 rounded-lg border ${service.selected ? 'border-pink-500 bg-pink-50' : 'border-gray-200'}`}
                    >
                      <span className={service.selected ? "font-medium" : ""}>{service.selected ? "+" : ""}{service.name}</span>
                      {showPrices && service.price && <span className="text-sm text-gray-600">{service.price}</span>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Looks Grid */}
            <Card>
              <CardContent className="p-6">
                <p className="font-medium mb-4">Select a Look</p>
                <div className="grid grid-cols-3 gap-2">
                  {looks.map((look, i) => (
                    <div key={i} className="relative">
                      <EditableImage
                        sectionId="hair-demo-looks"
                        field={`look_${i}`}
                        defaultImage={null}
                        className="aspect-square rounded-lg overflow-hidden"
                        placeholderClassName="bg-gradient-to-br from-pink-100 to-purple-100"
                        placeholderIcon={<ImageIcon className="w-6 h-6 text-pink-300" />}
                      />
                      {look.tag && (
                        <Badge className="absolute top-1 left-1 text-[8px] bg-pink-500">{look.tag}</Badge>
                      )}
                      <p className="text-[10px] mt-1 text-center">{look.name}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Customize Section */}
          <Card className="mt-6">
            <CardContent className="p-6">
              <h3 className="font-semibold mb-4">Customize</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-medium mb-2">Length</p>
                  <div className="space-y-1">
                    {lengths.map((len, i) => (
                      <div key={i} className="flex justify-between text-sm p-2 rounded bg-gray-50">
                        <span>{len.name}</span>
                        <span className="text-pink-600">{len.price}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium mb-2">Style Add-ons</p>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm p-2 rounded bg-gray-50">
                      <span>Classic Boho</span>
                      <span className="text-pink-600">+$40</span>
                    </div>
                    <div className="flex justify-between text-sm p-2 rounded bg-gray-50">
                      <span>Extra Large Parts</span>
                      <span className="text-pink-600">+$25</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* What You Need to Bring - Exact from PDF */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">What You Need to Bring</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="p-6">
                <p className="font-medium mb-2">Hair Type</p>
                <p className="text-sm text-gray-600 mb-4">Pre-stretched braiding hair</p>
                
                <p className="font-medium mb-2">Amount</p>
                <p className="text-sm text-gray-600 mb-4">7-9 packs</p>
                
                <p className="font-medium mb-2">Recommended Brands</p>
                <div className="flex flex-wrap gap-2">
                  {["X-Pression", "Outre", "FreeTress", "Amazon"].map(brand => (
                    <Badge key={brand} variant="outline">{brand}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-pink-50 border-pink-200">
              <CardContent className="p-6">
                <p className="font-medium mb-2">Provide It For Me</p>
                <p className="text-sm text-gray-600 mb-4">Stylist will have quality hair ready for you</p>
                <Badge className="bg-pink-500">Beauty Supply +$50</Badge>
              </CardContent>
            </Card>
          </div>

          {/* Before Your Appointment */}
          <Card className="mt-6">
            <CardContent className="p-6">
              <h3 className="font-semibold mb-4">Before Your Appointment</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-500" />
                  <span>Come with hair freshly washed & dried</span>
                  <Badge variant="destructive" className="ml-auto">Required</Badge>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-500" />
                  <span>No product in hair</span>
                  <Badge variant="destructive" className="ml-auto">Required</Badge>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-gray-300" />
                  <span>Detangled and blow-dried preferred</span>
                </div>
              </div>
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm font-medium">Hair Wash Service</p>
                <p className="text-xs text-gray-600">Can't wash beforehand? We offer wash service +$25</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Story Generator - Exact from PDF */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-pink-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-2">Story Generator</h2>
            <p className="text-gray-600">Share Your Menu on IG Stories</p>
            <p className="text-sm text-gray-500">Generate a beautiful story image with your services. Toggle prices, add promos.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Controls */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4">Customize Your Story</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span>$ Show Prices</span>
                    <Switch defaultChecked />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Add a Promo (optional)</p>
                    <p className="text-xs text-gray-400">e.g., 20% off for new clients!</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-2">Services to Feature</p>
                    <div className="space-y-1">
                      {services.slice(0, 5).map((s, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <input type="checkbox" defaultChecked className="rounded" />
                          <span className="text-sm">{s.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Preview */}
            <Card className="bg-gradient-to-b from-pink-500 to-purple-600 text-white">
              <CardContent className="p-6">
                <p className="text-sm opacity-80 mb-4">Current Services</p>
                <div className="space-y-2">
                  {services.filter(s => s.price).map((s, i) => (
                    <div key={i} className="flex justify-between">
                      <span>{s.selected ? "+" : ""}{s.name}</span>
                      <span>{s.price}</span>
                    </div>
                  ))}
                </div>
                <Button className="w-full mt-6 bg-white text-pink-600 hover:bg-gray-100">
                  <Download className="w-4 h-4 mr-2" />
                  Download Story Image
                </Button>
                <p className="text-xs text-center mt-2 opacity-70">1080x1920px Ready for IG Stories</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Client Preferences - Exact from PDF */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-2">Client Preferences</h2>
            <p className="text-gray-600">Know Exactly What They Want</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Style Preferences */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4">Style Preferences</h3>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium mb-2">Edges</p>
                    <div className="flex gap-2">
                      {["No edges", "Edges", "Dramatic edges"].map(opt => (
                        <Badge key={opt} variant="outline" className="cursor-pointer hover:bg-pink-50">{opt}</Badge>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium mb-2">Length</p>
                    <div className="flex flex-wrap gap-2">
                      {["Shoulder length", "Mid back", "Lower back", "Below the butt"].map(opt => (
                        <Badge key={opt} variant="outline" className="cursor-pointer hover:bg-pink-50">{opt}</Badge>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium mb-2">Ends</p>
                    <div className="flex gap-2">
                      {["No curls", "Curls at the end"].map(opt => (
                        <Badge key={opt} variant="outline" className="cursor-pointer hover:bg-pink-50">{opt}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Shade Selection */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4">Shade</h3>
                <div className="grid grid-cols-3 gap-2">
                  {shades.map((shade, i) => (
                    <div key={i} className="text-center p-2 rounded border hover:border-pink-500 cursor-pointer">
                      <div className="w-8 h-8 rounded-full bg-gray-800 mx-auto mb-1" style={{ backgroundColor: i === 3 ? '#d4a574' : undefined }} />
                      <p className="text-xs font-medium">{shade.code}</p>
                      <p className="text-[10px] text-gray-500">{shade.name}</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-3">Black/African hair shades: 1, 1B, 2, 2B, 4, 27, 30, 33</p>
              </CardContent>
            </Card>
          </div>

          {/* Vibe Preferences */}
          <Card className="mt-6">
            <CardContent className="p-6">
              <h3 className="font-semibold mb-4">Vibe Preferences</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {vibes.map((vibe, i) => (
                  <div key={i} className="text-center p-3 rounded-lg border hover:border-pink-500 cursor-pointer">
                    <p className="font-medium text-sm">{vibe.label}</p>
                    <p className="text-xs text-gray-500">{vibe.desc}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Make It Yours / Branding - Exact from PDF */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-pink-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">Make It Yours</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-2">Custom Colors</h3>
                <p className="text-sm text-gray-600">Match your brand</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-2">Your Logo</h3>
                <p className="text-sm text-gray-600">On stories & booking pages</p>
                <p className="text-xs text-pink-600 mt-2">↑ Upload your logo</p>
              </CardContent>
            </Card>
          </div>
          <p className="text-center text-sm text-gray-500 mt-4">Pro Plan Features</p>
        </div>
      </section>

      {/* CTA Section - Exact from PDF */}
      <section className="py-20 px-4 bg-gradient-to-r from-pink-500 to-purple-500 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Ready to Streamline Your Bookings?
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Join the pilot. DM to booked, seamlessly.
          </p>
          <Button
            onClick={onAccept}
            size="lg"
            className="bg-white text-pink-600 hover:bg-gray-100 h-14 px-10 rounded-full text-lg font-semibold"
          >
            Accept Invite
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>
    </div>
  );
}
